import { motion } from 'framer-motion';
import { MessageCircle, Palette, Code2, Rocket, HeadphonesIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const steps = [
  {
    icon: MessageCircle,
    title_fr: 'Consultation',
    title_en: 'Consultation',
    desc_fr: 'Nous analysons vos besoins, votre vision et vos objectifs pour définir ensemble le cahier des charges parfait.',
    desc_en: 'We analyze your needs, vision and goals to define the perfect specifications together.',
    color: '#4A90D9'
  },
  {
    icon: Palette,
    title_fr: 'Design & UX',
    title_en: 'Design & UX',
    desc_fr: 'Nous créons des maquettes et prototypes que vous validez avant le développement. Votre satisfaction est notre priorité.',
    desc_en: 'We create mockups and prototypes that you validate before development. Your satisfaction is our priority.',
    color: '#9B59B6'
  },
  {
    icon: Code2,
    title_fr: 'Développement',
    title_en: 'Development',
    desc_fr: 'Notre équipe code votre solution avec les technologies les plus modernes et performantes. Code propre et testé.',
    desc_en: 'Our team codes your solution with the most modern and performant technologies. Clean and tested code.',
    color: '#E67E22'
  },
  {
    icon: Rocket,
    title_fr: 'Lancement',
    title_en: 'Launch',
    desc_fr: 'Déploiement sur les stores ou mise en ligne de votre site. Nous gérons tout pour un lancement réussi.',
    desc_en: 'Deployment on stores or your website goes live. We handle everything for a successful launch.',
    color: '#27AE60'
  },
  {
    icon: HeadphonesIcon,
    title_fr: 'Support continu',
    title_en: 'Ongoing support',
    desc_fr: 'Maintenance, mises à jour et support technique inclus. Nous restons à vos côtés après le lancement.',
    desc_en: 'Maintenance, updates and technical support included. We stay by your side after launch.',
    color: '#E74C3C'
  }
];

const ProcessSection = () => {
  const { language } = useLanguage();

  return (
    <section id="process" className="py-20 bg-[#0F1B2D]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#4A90D9] text-sm font-semibold tracking-wider uppercase">
            {language === 'fr' ? 'Notre méthode' : 'Our method'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">
            {language === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
          </h2>
          <p className="text-white/60 mt-4 max-w-2xl mx-auto">
            {language === 'fr'
              ? "De l'idée au produit final, voici notre processus en 5 étapes simples."
              : 'From idea to final product, here is our process in 5 simple steps.'}
          </p>
        </motion.div>

        {/* Desktop: Horizontal timeline */}
        <div className="hidden lg:block">
          {/* Timeline line */}
          <div className="relative">
            <div className="absolute top-8 left-0 right-0 h-0.5 bg-white/10" />
            <div className="grid grid-cols-5 gap-6">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 }}
                  className="relative text-center"
                >
                  {/* Step number circle */}
                  <div className="relative z-10 mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: `${step.color}20`, border: `2px solid ${step.color}40` }}>
                    <step.icon size={26} style={{ color: step.color }} />
                  </div>
                  
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: step.color }}>
                    {language === 'fr' ? `Étape ${index + 1}` : `Step ${index + 1}`}
                  </span>
                  <h3 className="text-lg font-bold text-white mt-2 mb-3">
                    {language === 'fr' ? step.title_fr : step.title_en}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {language === 'fr' ? step.desc_fr : step.desc_en}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: Vertical timeline */}
        <div className="lg:hidden space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex gap-5"
              data-testid={`process-step-${index + 1}`}
            >
              {/* Left: Icon + Line */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${step.color}20`, border: `2px solid ${step.color}40` }}>
                  <step.icon size={22} style={{ color: step.color }} />
                </div>
                {index < steps.length - 1 && <div className="w-0.5 flex-1 mt-3 bg-white/10" />}
              </div>

              {/* Right: Content */}
              <div className="pb-4">
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: step.color }}>
                  {language === 'fr' ? `Étape ${index + 1}` : `Step ${index + 1}`}
                </span>
                <h3 className="text-lg font-bold text-white mt-1 mb-2">
                  {language === 'fr' ? step.title_fr : step.title_en}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {language === 'fr' ? step.desc_fr : step.desc_en}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
